/**
 * HISIP-005: Stage Settings Grid Component
 * 
 * Grid for configuring personas, rubrics, providers, and styles for each pipeline stage
 */

// External imports
import { User, Zap, PenTool, Eye, Grid, Film, Compass, Image, Layers } from 'lucide-react';
import React from 'react';

// Local imports
import FuturisticSelect from '@/components/ui/FuturisticSelect';
// Type imports
import type { StageSettingsInput, Stage } from '@/hooks/useProfiles';
import { type GroupedTemplates } from '@/hooks/useTemplates';

interface StageSettingsGridProps {
  stageSettings: Record<string, StageSettingsInput>;
  templates: GroupedTemplates;
  onChange: (stage: string, field: string, value: string) => void;
}

const STAGES = [
  { key: 'script', name: 'Script', description: 'Generate script content from curriculum' },
  { key: 'vision', name: 'Vision', description: 'Create visual direction and style guide' },
  { key: 'storyboard', name: 'Storyboard', description: 'Plan visual sequence and composition' },
  { key: 'shots', name: 'Shot List', description: 'Break down into individual shots' },
  { key: 'continuity', name: 'Continuity', description: 'Ensure visual consistency across shots' },
  { key: 'frames', name: 'Frames', description: 'Generate start and end frame images' },
  { key: 'video', name: 'Video', description: 'Create final video from frames' }
];

const STAGE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  script: PenTool,
  vision: Eye,
  storyboard: Grid,
  shots: Film,
  continuity: Compass,
  frames: Image,
  video: Layers
};

const STAGE_ICON_COLORS: Record<string, string> = {
  script: 'text-purple-400',
  vision: 'text-cyan-400',
  storyboard: 'text-emerald-400',
  shots: 'text-amber-400',
  continuity: 'text-red-400',
  frames: 'text-indigo-400',
  video: 'text-teal-400'
};

const PROVIDERS = [
  // Verified via vendor docs: Anthropic/OpenAI/Google
  { key: 'anthropic', name: 'Anthropic', models: ['claude-opus-4-1-20250805', 'claude-sonnet-4-20250514'] },
  { key: 'openai', name: 'OpenAI', models: ['gpt-5', 'gpt-5-mini'] },
  { key: 'google', name: 'Google', models: ['gemini-2.5-pro', 'gemini-2.5-flash'] }
];

const StageSettingsGrid: React.FC<StageSettingsGridProps> = ({
  stageSettings,
  templates,
  onChange
}) => {
  const getStageSettings = (stageKey: string): StageSettingsInput => {
    return stageSettings[stageKey] || {
      stage: stageKey as Stage,
      writer_persona_id: '',
      evaluator_persona_id: '',
      rubric_id: '',
      provider: '',
      model: '',
      default_style_id: null
    };
  };

  // Single model list across providers
  const ALL_MODELS: string[] = React.useMemo(
    () => PROVIDERS.flatMap(p => p.models),
    []
  );
  const MODEL_TO_PROVIDER: Record<string, string> = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const p of PROVIDERS) {
      for (const m of p.models) {
        map[m] = p.key;
      }
    }
    return map;
  }, []);

  // Styles are applied globally from Style DNA; no per-stage default now

  const renderDropdown = (
    stage: string,
    field: string,
    value: string,
    options: Array<{ value: string; label: string; description?: string }>,
    placeholder: string,
    icon: React.ReactNode,
    displayLabel?: string
  ) => (
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <label className="text-xs font-mono text-cyan-400 uppercase tracking-wider">
          {displayLabel || field.replace('_', ' ')}
        </label>
      </div>
      
      <FuturisticSelect
        value={value}
        onChange={(v) => onChange(stage, field, v)}
        options={options}
        placeholder={placeholder}
      />
      
      {/* Show description if item is selected */}
      {value && (
        <div className="mt-2 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded text-xs text-cyan-400 font-mono">
          {options.find(opt => opt.value === value)?.description || 'No description available'}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Stage Rows */}
      {STAGES.map((stage) => {
        const settings = getStageSettings(stage.key);
        
        return (
          <div
            key={stage.key}
            className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 bg-black/20 rounded-lg border border-white/10 hover:border-cyan-500/20 transition-colors"
          >
            {/* Stage Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                {(() => {
                  const Icon = STAGE_ICONS[stage.key] || PenTool;
                  const color = STAGE_ICON_COLORS[stage.key] || 'text-cyan-400';
                  return <Icon className={`w-4 h-4 ${color}`} />;
                })()}
                <h3 className="text-white/90 font-mono text-sm">{stage.name}</h3>
              </div>
              <p className="text-xs text-white/50 font-mono leading-relaxed">
                {stage.description}
              </p>
            </div>

            {/* Artist */}
            <div className="lg:col-span-4">
              {renderDropdown(
                stage.key,
                'writer_persona_id',
                settings.writer_persona_id,
                templates.personas
                  .filter(p => (!p.stage || p.stage === stage.key) && (!p.role || p.role === 'artist'))
                  .map(p => ({
                    value: p.slug,
                    label: p.name,
                    description: p.description
                  })),
                'Select Artist',
                <User className="w-4 h-4 text-purple-400" />,
                'Artist'
              )}
            </div>
            
            {/* Evaluator */}
            <div className="lg:col-span-3">
              {renderDropdown(
                stage.key,
                'evaluator_persona_id',
                settings.evaluator_persona_id,
                templates.personas
                  .filter(p => (!p.stage || p.stage === stage.key) && (!p.role || p.role === 'evaluator'))
                  .map(p => ({
                    value: p.slug,
                    label: p.name,
                    description: p.description
                  })),
                'Select Evaluator',
                <User className="w-4 h-4 text-blue-400" />,
                'Evaluator'
              )}
            </div>

            {/* Model only */}
            <div className="lg:col-span-3">
              <div className="relative">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-yellow-400" />
                  <label className="text-xs font-mono text-cyan-400 uppercase tracking-wider">
                    Model
                  </label>
                </div>
                <FuturisticSelect
                  value={settings.model}
                  onChange={(newModel) => {
                    const provider = MODEL_TO_PROVIDER[newModel] || '';
                    onChange(stage.key, 'model', newModel);
                    if (provider && provider !== settings.provider) {
                      onChange(stage.key, 'provider', provider);
                    }
                  }}
                  options={ALL_MODELS.map((m) => ({ value: m, label: m }))}
                  placeholder="Select Model"
                />
              </div>
            </div>

            {/* Default Style removed; styles applied globally from Style DNA */}
          </div>
        );
      })}

      {/* Removed configuration requirements panel by request */}
    </div>
  );
};

export default StageSettingsGrid;